import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { changePassword } from '../../services/authService';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      full_name: user?.full_name || '',
      email: user?.email || '',
      username: user?.username || ''
    }
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Update user profile
      updateUser(data);
      toast.success('Profil berhasil diperbarui');
    } catch (error) {
      toast.error('Gagal memperbarui profil');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async (data) => {
    setIsSubmitting(true);
    try {
      await changePassword(data);
      toast.success('Password berhasil diubah');
      setShowPasswordForm(false);
      reset();
    } catch (error) {
      toast.error('Gagal mengubah password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Profil Pengguna</h2>
      </div>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Informasi Profil</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nama Lengkap *</Form.Label>
                      <Form.Control
                        type="text"
                        {...register('full_name', { required: 'Nama lengkap wajib diisi' })}
                        isInvalid={!!errors.full_name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.full_name?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        value={user?.username || ''}
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Username tidak dapat diubah
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        {...register('email', { 
                          required: 'Email wajib diisi',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Email tidak valid'
                          }
                        })}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Control
                        type="text"
                        value={user?.role || ''}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button variant="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5>Ubah Password</h5>
            </Card.Header>
            <Card.Body>
              {!showPasswordForm ? (
                <Button 
                  variant="outline-primary" 
                  onClick={() => setShowPasswordForm(true)}
                >
                  Ubah Password
                </Button>
              ) : (
                <PasswordChangeForm 
                  onSubmit={handlePasswordChange}
                  onCancel={() => setShowPasswordForm(false)}
                  isSubmitting={isSubmitting}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card>
            <Card.Header>
              <h5>Informasi Akun</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Status Akun:</strong>
                <br />
                <span className={`badge bg-${user?.is_active ? 'success' : 'secondary'}`}>
                  {user?.is_active ? 'Aktif' : 'Nonaktif'}
                </span>
              </div>
              
              <div className="mb-3">
                <strong>Terakhir Login:</strong>
                <br />
                <small className="text-muted">
                  {user?.last_login ? new Date(user.last_login).toLocaleString('id-ID') : 'Belum pernah login'}
                </small>
              </div>

              <div className="mb-3">
                <strong>Bergabung Sejak:</strong>
                <br />
                <small className="text-muted">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('id-ID') : '-'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Password Change Form Component
const PasswordChangeForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm();

  const newPassword = watch('new_password');

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Group className="mb-3">
        <Form.Label>Password Lama *</Form.Label>
        <Form.Control
          type="password"
          {...register('current_password', { required: 'Password lama wajib diisi' })}
          isInvalid={!!errors.current_password}
        />
        <Form.Control.Feedback type="invalid">
          {errors.current_password?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Password Baru *</Form.Label>
        <Form.Control
          type="password"
          {...register('new_password', { 
            required: 'Password baru wajib diisi',
            minLength: {
              value: 6,
              message: 'Password minimal 6 karakter'
            }
          })}
          isInvalid={!!errors.new_password}
        />
        <Form.Control.Feedback type="invalid">
          {errors.new_password?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Konfirmasi Password Baru *</Form.Label>
        <Form.Control
          type="password"
          {...register('confirm_password', { 
            required: 'Konfirmasi password wajib diisi',
            validate: value => value === newPassword || 'Password tidak cocok'
          })}
          isInvalid={!!errors.confirm_password}
        />
        <Form.Control.Feedback type="invalid">
          {errors.confirm_password?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Batal
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Mengubah...' : 'Ubah Password'}
        </Button>
      </div>
    </Form>
  );
};

export default Profile; 